import type { MapRef } from "@vis.gl/react-maplibre";
import { FC, useEffect, useMemo } from "react";
import { useRecoilState } from "recoil";

import {
  collectPolygonLocations,
  IMapLibrePolygonItem,
  MapLibrePolygonsLayer,
  useMapLibreLocationsAutoFit,
} from "@ryde/controls";
import { shownAreaPolygonsAtom } from "@ryde/shared";

import {
  FIT_BOUNDS_PADDING_PX,
  STATIONS_AREA_POLYGONS_STYLE,
} from "./stations-map.constants";
import { useStationsMapSelectedStations } from "./use-stations-map-selected-stations";

interface IStationsAreaPolygonsLayerProps {
  mapRef: React.RefObject<MapRef>;
  isMapLoaded: boolean;
}

export const StationsAreaPolygonsLayer: FC<IStationsAreaPolygonsLayerProps> = ({
  mapRef,
  isMapLoaded,
}) => {
  const [shownPolygons, setShownPolygons] = useRecoilState(
    shownAreaPolygonsAtom,
  );
  const { selectedStations } = useStationsMapSelectedStations();

  // Area ids that still have at least one station selected in the grid.
  const selectedAreaIds = useMemo(
    () =>
      new Set(
        selectedStations
          .filter(station => station.areaId)
          .map(station => station.areaId as number),
      ),
    [selectedStations],
  );

  // Drop any shown polygon whose area no longer has a selected station in the grid.
  useEffect(() => {
    setShownPolygons(prev => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([areaId]) =>
          selectedAreaIds.has(Number(areaId)),
        ),
      );

      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  }, [selectedAreaIds, setShownPolygons]);

  const polygons = useMemo<IMapLibrePolygonItem[]>(
    () =>
      Object.entries(shownPolygons).map(([areaId, { value, colorValue }]) => ({
        id: Number(areaId),
        polygon: value,
        color: colorValue,
      })),
    [shownPolygons],
  );

  /*
   * Refit only when the SET of shown areas changes — toggling one on or off — not whenever the polygons
   * re-render. The Leaflet version compared a sorted id signature for the same reason; here the shared
   * auto-fit already refits on a change of locations, so the memo keyed on that signature is what keeps a
   * re-render from re-framing the map under the user.
   */
  const shownAreasKey = useMemo(
    () =>
      Object.keys(shownPolygons)
        .map(Number)
        .sort((a, b) => a - b)
        .join(","),
    [shownPolygons],
  );

  const fitLocations = useMemo(
    () => collectPolygonLocations(polygons),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shownAreasKey],
  );

  useMapLibreLocationsAutoFit({
    mapRef,
    locations: fitLocations,
    isMapLoaded,
    // The page root already listens for this map resize; a second subscription would just resize twice.
    resizeEvent: null,
    fitPadding: FIT_BOUNDS_PADDING_PX,
  });

  return (
    <MapLibrePolygonsLayer
      id="stations-area-polygons"
      polygons={polygons}
      style={STATIONS_AREA_POLYGONS_STYLE}
    />
  );
};
